import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, ExternalLink, Store } from 'lucide-react';
import { slugify } from '@garlic/shared';
import { errorMessage, fieldErrors, post } from '../lib/api';
import { Alert, Button, Card, CardHeader, Field, Input, PageHeader, Select } from '../ui/primitives';
import { useToast } from '../ui/feedback';
import { SlugField } from './SlugField';

const TIMEZONES = [
  ['America/Sao_Paulo', 'Brasília (SP, RJ, MG, Sul, NE…)'],
  ['America/Manaus', 'Amazonas (AM, RR, RO)'],
  ['America/Cuiaba', 'Mato Grosso (MT, MS)'],
  ['America/Belem', 'Pará e Amapá'],
  ['America/Fortaleza', 'Ceará, RN, PB'],
  ['America/Recife', 'Pernambuco'],
  ['America/Bahia', 'Bahia'],
  ['America/Rio_Branco', 'Acre'],
  ['America/Noronha', 'Fernando de Noronha'],
];

type Created = { id: string; slug: string; url: string; panelUrl: string; loginUrl: string; inviteUrl?: string; inviteSentByEmail: boolean; existingUser: boolean };

export function RestaurantCreate() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState({ name: '', slug: '', adminName: '', adminEmail: '', phone: '', legalName: '', document: '', city: '', state: '', timezone: 'America/Sao_Paulo' });
  const [slugTouched, setSlugTouched] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<Created>();

  const set = (k: keyof typeof form) => (v: string) => {
    setForm((f) => ({ ...f, [k]: v, ...(k === 'name' && !slugTouched ? { slug: slugify(v) } : {}) }));
    setErrors((e) => ({ ...e, [k]: '' }));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const r = await post<Created>('/api/dev/restaurants', form);
      await qc.invalidateQueries({ queryKey: ['dev-restaurants'] });
      await qc.invalidateQueries({ queryKey: ['dev-dashboard'] });
      setCreated(r);
      toast.success('Restaurante cadastrado');
    } catch (err) {
      setErrors(fieldErrors(err));
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (created) return <CreatedPanel created={created} name={form.name} email={form.adminEmail} onOpen={() => navigate(`/dev/restaurantes/${created.id}`)} />;

  return (
    <>
      <PageHeader eyebrow={<Link to="/dev/restaurantes" className="inline-flex items-center gap-1 hover:text-ink"><ArrowLeft className="size-3.5" /> Restaurantes</Link>} title="Novo restaurante" description="Cria o ambiente do estabelecimento e convida o administrador por e-mail." />
      <form onSubmit={submit} className="space-y-6" noValidate>
        {error && <Alert tone="danger">{error}</Alert>}
        <Card>
          <CardHeader title="Estabelecimento" />
          <div className="grid gap-4 p-5 sm:grid-cols-2">
            <Field label="Nome comercial" error={errors.name}>{(id) => <Input id={id} required value={form.name} invalid={!!errors.name} onChange={(e) => set('name')(e.target.value)} />}</Field>
            <SlugField
              value={form.slug}
              serverError={errors.slug}
              onChange={(v) => {
                setSlugTouched(true);
                set('slug')(v);
              }}
            />
            <Field label="Razão social (opcional)" error={errors.legalName}>{(id) => <Input id={id} value={form.legalName} onChange={(e) => set('legalName')(e.target.value)} />}</Field>
            <Field label="CNPJ/CPF (opcional)" error={errors.document}>{(id) => <Input id={id} inputMode="numeric" value={form.document} onChange={(e) => set('document')(e.target.value)} />}</Field>
            <Field label="Telefone" error={errors.phone}>{(id) => <Input id={id} type="tel" required placeholder="(11) 99999-9999" value={form.phone} invalid={!!errors.phone} onChange={(e) => set('phone')(e.target.value)} />}</Field>
            <Field label="Fuso horário" error={errors.timezone}>
              {(id) => (
                <Select id={id} value={form.timezone} onChange={(e) => set('timezone')(e.target.value)}>
                  {TIMEZONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </Select>
              )}
            </Field>
            <Field label="Cidade (opcional)">{(id) => <Input id={id} value={form.city} onChange={(e) => set('city')(e.target.value)} />}</Field>
            <Field label="UF (opcional)" error={errors.state}>{(id) => <Input id={id} maxLength={2} className="uppercase" value={form.state} onChange={(e) => set('state')(e.target.value.toUpperCase())} />}</Field>
          </div>
        </Card>
        <Card>
          <CardHeader title="Administrador do restaurante" description="Recebe um convite para definir a própria senha. O Garlic nunca define senhas por ele." />
          <div className="grid gap-4 p-5 sm:grid-cols-2">
            <Field label="Nome" error={errors.adminName}>{(id) => <Input id={id} required value={form.adminName} invalid={!!errors.adminName} onChange={(e) => set('adminName')(e.target.value)} />}</Field>
            <Field label="E-mail" error={errors.adminEmail}>{(id) => <Input id={id} type="email" required value={form.adminEmail} invalid={!!errors.adminEmail} onChange={(e) => set('adminEmail')(e.target.value)} />}</Field>
          </div>
        </Card>
        <div className="flex justify-end gap-2">
          <Link to="/dev/restaurantes"><Button variant="secondary">Cancelar</Button></Link>
          <Button type="submit" loading={busy} icon={<Store className="size-4" />}>Cadastrar restaurante</Button>
        </div>
      </form>
    </>
  );
}

function CreatedPanel({ created, name, email, onOpen }: { created: Created; name: string; email: string; onOpen: () => void }) {
  const toast = useToast();
  return (
    <Card className="mx-auto max-w-2xl p-6 sm:p-8">
      <h1 className="font-display text-2xl font-semibold">{name} foi cadastrado</h1>
      <p className="mt-1 text-ink-3">Já está no ar no domínio principal, sem nenhuma configuração de DNS ou servidor. Ele nasce em implantação: a equipe já configura, e o cardápio mostra "em breve" até você ativar.</p>
      <dl className="mt-6 space-y-4 text-sm">
        <div>
          <dt className="text-ink-3">Endereço do restaurante</dt>
          <dd className="mt-1"><a className="inline-flex items-center gap-1 font-medium text-garlic-600 hover:underline" href={created.url} target="_blank" rel="noreferrer">{created.url} <ExternalLink className="size-3.5" /></a></dd>
          <dt className="mt-3 text-ink-3">Painel administrativo</dt>
          <dd className="mt-1"><a className="inline-flex items-center gap-1 font-medium text-garlic-600 hover:underline" href={created.panelUrl} target="_blank" rel="noreferrer">{created.panelUrl} <ExternalLink className="size-3.5" /></a></dd>
        </div>
        <div>
          <dt className="text-ink-3">Convite do administrador</dt>
          <dd className="mt-1">
            {created.existingUser ? (
              <Alert tone="info">
                {email} já tem acesso ao Garlic em outro restaurante e entra com a senha que já usa em{' '}
                <a className="font-medium underline" href={created.loginUrl} target="_blank" rel="noreferrer">{created.loginUrl}</a>.
              </Alert>
            ) : created.inviteSentByEmail ? (
              <Alert tone="success">Convite enviado para {email}.</Alert>
            ) : (
              <div className="space-y-2">
                <Alert tone="warning" title="E-mail não configurado">Envie este link para {email} por um canal seguro. Ele vale por 7 dias e só pode ser usado uma vez.</Alert>
                <div className="flex gap-2">
                  <Input readOnly value={created.inviteUrl} className="font-mono text-xs" onFocus={(e) => e.currentTarget.select()} aria-label="Link de convite" />
                  <Button variant="secondary" icon={<Copy className="size-4" />} onClick={() => navigator.clipboard.writeText(created.inviteUrl!).then(() => toast.success('Link copiado'))}>Copiar</Button>
                </div>
              </div>
            )}
          </dd>
        </div>
      </dl>
      <div className="mt-8 flex justify-end">
        <Button onClick={onOpen}>Ver restaurante</Button>
      </div>
    </Card>
  );
}
